/* eslint-disable no-bitwise */
import {
    Logger, namespace, Point, Bounds, Matrix,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';

namespace('Ofcs.Editor.Item');

/**
 * @class CutsquareItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.CutsquareItem = AbstractItem.extend({

    type: 'cutsquare',

    sealingOptions: ['without', 'with'],

    /**
     * Constant for minimum height and width of a rectangle bag
     *
     * @constant
     * @property {number} MIN_SIZE
     */
    MIN_SIZE: 300,

    EDGE_TOP: 1,
    EDGE_RIGHT: 2,
    EDGE_BOTTOM: 4,
    EDGE_LEFT: 8,

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {Ofcs.Editor.Item.CircleItem} item Me
     */

    defaults: {
        /**
         * @property {String} Type
         */
        type: 'Cutsquare',

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Current rotation
         * @property {number} attributes.rotation
         */
        rotation: 0,

        /**
         * Width of the shape
         * @property {number} attributes.width
         */
        width: 0,

        /**
         * Height of the shape
         * @property {number} attributes.height
         */
        height: 0,

        /**
         * Label
         * @property {String} attributes.userLabel
         */
        userLabel: '',

        /**
         * Current tool version:
         * stores binary status for edge situations
         * @property {String} attributes.toolVersion
         */
        toolVersion: 0,

        /**
         * Sealing
         * @property {String} attributes.sealing
         */
        sealing: 'without',

        /**
         * Defines it item is billable
         * Will be set and checked by tabletopCollisionManager
         * @property {boolean} attributes.billable
         */
        billable: true,

        area: 0,
    },

    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.supportsWidth = true;
        this.supportsHeight = true;
        this.supportsRotation = false;
        this.supportsPositioning = true;
        this.supportsDepth = false;
        this.supportsCollisions = true;
        this.supportsOutOfFoam = true;
        this.supportsBidirectionalCollisions = true;
        this.supportsInsideTable = 'edge';
        this.supportsGuides = true;
        this.supportsSealing = true;

        // Initialize paths by setting the toolVersion
        // this.set('toolVersion', this.get('toolVersion'));
    },

    /**
     * Parses response data into attributes
     *
     * @param {Object} response Response hash
     * @return {Object} Attribute hash
     */
    parse(response) {
        AbstractItem.prototype.parse.call(this, response);

        if (response.version !== undefined && response.toolVersion === undefined) {
            response.toolVersion = Number(response.version);

            delete response.version;
        }

        return response;
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        return Math.max(this.get('width'), this.get('height')) * this.getMarginFactor(this.get('toolVersion'));
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * Overwrites AbstractItem's getMarginBounds to prevent caching of bounds
     * calculations for dynamic margin cutsquare object
     *
     * @return {Bounds} Bounding box of the item
     */
    getMarginBounds() {
        const segments = this.get('safety');
        const points = this.getPoints(segments);

        const baseBounds = new Bounds(...points);

        if (this.supportsPositioning) {
            const matrix = new Matrix();
            const position = this.get('position');

            matrix.translate(-position.x, -position.y);
            baseBounds.transform(matrix);
        }

        const positionedBounds = baseBounds.clone();

        if (this.supportsPositioning) {
            const matrix = new Matrix();
            const position = this.get('position');

            matrix.translate(position.x, position.y);
            positionedBounds.transform(matrix);
        }

        return positionedBounds;
    },

    calculateSegments(width, height, toolVersion, withMargin) {
        const xMargin = height * this.getMarginFactor(toolVersion);
        const yMargin = width * this.getMarginFactor(toolVersion);
        const path = [];

        const halfs = {
            top: -height / 2,
            right: width / 2,
            bottom: height / 2,
            left: -width / 2,
        };

        if (withMargin) {
            if (!(toolVersion & this.EDGE_TOP)) halfs.top -= yMargin;
            if (!(toolVersion & this.EDGE_BOTTOM)) halfs.bottom += yMargin;
            if (!(toolVersion & this.EDGE_LEFT)) halfs.left -= xMargin;
            if (!(toolVersion & this.EDGE_RIGHT)) halfs.right += xMargin;
        }

        path.push(['M', halfs.left, halfs.top]);
        path.push(['L', halfs.right, halfs.top]);
        path.push(['L', halfs.right, halfs.bottom]);
        path.push(['L', halfs.left, halfs.bottom]);
        path.push(['L', halfs.left, halfs.top]);

        return [path];
    },

    set(key, value, optionsParam) {
        let attrs = {};
        let options = optionsParam;

        // Handle both `"key", value` and `{key: value}` -style arguments.
        if (typeof key === 'object') {
            attrs = key;
            options = value;
        } else {
            attrs[key] = value;
        }

        options = options || {};

        if (attrs.width !== undefined) {
            attrs.width = Math.max(this.MIN_SIZE, attrs.width);
            Logger.debug('rectangle: fixed width', attrs.width);
        }

        if (attrs.height !== undefined) {
            attrs.height = Math.max(this.MIN_SIZE, attrs.height);
            Logger.debug('rectangle: fixed height', attrs.height);
        }

        if (attrs.width !== undefined || attrs.height !== undefined || attrs.toolVersion !== undefined) {
            const {
                width = this.get('width'),
                height = this.get('height'),
                toolVersion = this.get('toolVersion'),
            } = attrs;

            const segmentsWithoutMargin = this.calculateSegments(width, height, toolVersion, false);
            attrs = {
                ...attrs,
                ext: segmentsWithoutMargin,
                orig: segmentsWithoutMargin,
                safety: this.calculateSegments(width, height, toolVersion, true),
            };
        }

        const result = AbstractItem.prototype.set.call(this, attrs, options);

        if (attrs.width !== undefined || attrs.height !== undefined || attrs.toolVersion !== undefined) {
            this.trigger('updatesegments', this);
        }

        return result;
    },

    /**
     * Get margin factor for dynamic margin calculation
     *
     * @param {number} [toolVersion]
     * @return {number} factor for margin calculation
     */
    getMarginFactor(toolVersion = this.get('toolVersion')) {
        if (toolVersion === 0) {
            return 0.2;
        }
        return 0.25;
    },

    /**
     * Get top-middle point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTop(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const topHalf = withMargin && !this.hasEdge(this.EDGE_TOP)
            ? (this.get('height') / 2) + (this.get('width') * this.getMarginFactor())
            : this.get('height') / 2;
        const point = new Point(center.x, center.y - topHalf);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-middle point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottom(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const bottomHalf = withMargin && !this.hasEdge(this.EDGE_BOTTOM)
            ? (this.get('height') / 2) + (this.get('width') * this.getMarginFactor())
            : this.get('height') / 2;
        const point = new Point(center.x, center.y + bottomHalf);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const leftHalf = withMargin && !this.hasEdge(this.EDGE_LEFT)
            ? (this.get('width') / 2) + (this.get('height') * this.getMarginFactor())
            : this.get('width') / 2;
        const point = new Point(center.x - leftHalf, center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const rightHalf = withMargin && !this.hasEdge(this.EDGE_RIGHT)
            ? (this.get('width') / 2) + (this.get('height') * this.getMarginFactor())
            : this.get('width') / 2;
        const point = new Point(center.x + rightHalf, center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const leftHalf = withMargin && !this.hasEdge(this.EDGE_LEFT)
            ? (this.get('width') / 2) + (this.get('height') * this.getMarginFactor())
            : this.get('width') / 2;
        const topHalf = withMargin && !this.hasEdge(this.EDGE_TOP)
            ? (this.get('height') / 2) + (this.get('width') * this.getMarginFactor())
            : this.get('height') / 2;
        const point = new Point(center.x - leftHalf, center.y - topHalf);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const rightHalf = withMargin && !this.hasEdge(this.EDGE_RIGHT)
            ? (this.get('width') / 2) + (this.get('height') * this.getMarginFactor())
            : this.get('width') / 2;
        const topHalf = withMargin && !this.hasEdge(this.EDGE_TOP)
            ? (this.get('height') / 2) + (this.get('width') * this.getMarginFactor())
            : this.get('height') / 2;
        const point = new Point(center.x + rightHalf, center.y - topHalf);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const leftHalf = withMargin && !this.hasEdge(this.EDGE_LEFT)
            ? (this.get('width') / 2) + (this.get('height') * this.getMarginFactor())
            : this.get('width') / 2;
        const bottomHalf = withMargin && !this.hasEdge(this.EDGE_BOTTOM)
            ? (this.get('height') / 2) + (this.get('width') * this.getMarginFactor())
            : this.get('height') / 2;
        const point = new Point(center.x - leftHalf, center.y + bottomHalf);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const rightHalf = withMargin && !this.hasEdge(this.EDGE_RIGHT)
            ? (this.get('width') / 2) + (this.get('height') * this.getMarginFactor())
            : this.get('width') / 2;
        const bottomHalf = withMargin && !this.hasEdge(this.EDGE_BOTTOM)
            ? (this.get('height') / 2) + (this.get('width') * this.getMarginFactor())
            : this.get('height') / 2;
        const point = new Point(center.x + rightHalf, center.y + bottomHalf);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get minimum with.
     *
     * @return {number}
     */
    getMinWidth() {
        return this.MIN_SIZE;
    },

    /**
     * Get minimum height.
     *
     * @return {number}
     */
    getMinHeight() {
        return this.MIN_SIZE;
    },

    /**
     * Get minimum depth defined in parent item.
     *
     * @return {number}
     */
    getMinDepth() {
        const parentItem = this.getParentItem();

        return parentItem ? parentItem.get('minDepth') : 0;
    },

    /**
     * Get maximum depth defined in parent item.
     *
     * @return {number}
     */
    getMaxDepth() {
        const parentItem = this.getParentItem();

        return parentItem ? parentItem.get('depth') : 9999;
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, ...args);
        return {
            x: attributes.x,
            y: attributes.y,
            width: attributes.width,
            height: attributes.height,
            type: attributes.type,
            version: attributes.toolVersion,
            sealing: attributes.sealing,
            billable: attributes.billable,
        };
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {Ofcs.Editor.Svg.Handle.ResizeRectangleHandle}
     */
    initializeHandles(canvas) {
        return new Ofcs.Editor.Svg.Handle.ResizeRectangleHandle(this, canvas);
    },

    /**
     * Clear cache on change.
     *
     * @param {Object} changes
     */
    clearCache(changes) {
        if (!this.cache) return;
        AbstractItem.prototype.clearCache.call(this, changes);

        if (changes.toolVersion !== undefined) {
            this.cache.remove('baseBounds');
            this.cache.remove('baseMarginBounds');
            this.cache.remove('bounds');
            this.cache.remove('marginBounds');
            this.cache.remove('bagLines');
            this.cache.remove('marginLines');
            this.cache.remove('origLines');
        }
    },

    /**
     * Get the area of the item
     *
     * @return {number}
     */
    getArea() {
        return this.get('area');
    },

    /**
     * Checks if given Side is currently in edge position
     *
     * example:
     * this.hasEdge(this.EDGE_TOP)
     *
     * @param {number} edge
     * @return {boolean}
     */
    hasEdge(edge) {
        return (this.get('toolVersion') & edge) !== 0;
    },

    /**
     * Will update item's edges and set a corresponding
     * toolVersion when they change
     *
     * @param {number} x , 1 = right, 3 = left
     * @param {number} y , 0 = top, 2 = bottom
     */
    updateEdge(x, y) {
        // eslint-disable-next-line no-param-reassign
        x = x === undefined ? 0 : 2 ** x;
        // eslint-disable-next-line no-param-reassign
        y = y === undefined ? 0 : 2 ** y;

        const newToolVersion = x | y;

        if (this.get('toolVersion') !== newToolVersion) {
            this.set('toolVersion', newToolVersion);
        }
    },

    /**
     * Checks all edges and returns array with
     * all edges in snapped status
     *
     * @return {Array} Array with affected positions: 0 = top, 1 = right, 2 = bottom, 3 = left
     */
    getSnappedEdges() {
        const edges = [this.EDGE_TOP, this.EDGE_RIGHT, this.EDGE_BOTTOM, this.EDGE_LEFT];
        return edges
            .filter(edge => this.hasEdge(edge))
            .map(edge => edges.indexOf(edge));
    },

});
